const STORAGE_KEY = "atira:pending-signup";

/**
 * Deadline (epoch ms) the "resend code" button stays disabled until. Persisted
 * so a refresh on the code step resumes the countdown instead of restarting it.
 * Consumed by AtiraVerificationInput via its `cooldownStorageKey` prop.
 */
export const RESEND_COOLDOWN_KEY = "atira:signup-resend-cooldown";

// A pending signup is only worth resuming for as long as the backend could
// still recover it (the verification code / pending account TTL is short, and
// resend can mint a fresh one). After this we drop it and send the user back
// to the first step.
// Make sure to update this value if the TTL in user.model.ts changes
const MAX_AGE = 30 * 60 * 1000; // 30 minutes

type PendingSignup = {
  name: string;
  email: string;
};

type StoredPendingSignup = PendingSignup & {
  at: number;
};

/**
 * Remembers that the user has requested a verification code and is now on the
 * code-entry step, so a page refresh resumes there instead of dropping them
 * back on the name/email/password form. Dont store the password or the code.
 */
export const savePendingSignup = ({ name, email }: PendingSignup): void => {
  try {
    const payload: StoredPendingSignup = { name, email, at: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can be unavailable (private mode, disabled storage) -
    // resuming after a refresh is a nice-to-have, not something to crash on.
  }
};

export const readPendingSignup = (): PendingSignup | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredPendingSignup>;
    if (!parsed?.email || typeof parsed.at !== "number") {
      return null;
    }

    if (Date.now() - parsed.at > MAX_AGE) {
      clearPendingSignup();
      return null;
    }

    return { name: parsed.name ?? "", email: parsed.email };
  } catch {
    return null;
  }
};

export const clearPendingSignup = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(RESEND_COOLDOWN_KEY);
  } catch {
    // ignore - see savePendingSignup
  }
};
